import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { hasLicensedModule, PROGRAM_MODULES } from "../../core/module-license";
import DocExpSellBookModal from "../doc/exp/doc-exp-sell-book-modal";
import { formatMoney, getLawFlag, pick, toNumber } from "../doc/exp/doc-exp-utils";
import { VID_PLAT_META } from "../doc/plat/doc-plat-utils";
import { fetchHomeDashboard } from "./home-api";
import plusSrc from "./assets/plus.svg";
import stethoscopeSrc from "./assets/stethoscope.svg";
import arrowDownLeftSrc from "./assets/arrow-down-left.svg";
import hospitalSrc from "./assets/hospital.svg";
import walletSrc from "./assets/wallet.svg";
import alertTriangleSrc from "./assets/alert-triangle.svg";
import clockSrc from "./assets/clock.svg";
import sparklesSrc from "./assets/sparkles.svg";
import "./home.css";

const SESSIONS = [
  {
    patient: "Михайлов И. А.",
    procedure: "Грязелечение",
    place: "Каб. 104 · Орлова В.",
    time: "11:15",
    status: "waiting",
    statusLabel: "Ожидает"
  },
  {
    patient: "Смирнова О. Н.",
    procedure: "Ингаляция щелочная",
    place: "Каб. 212 · Петров А.",
    time: "11:30",
    status: "progress",
    statusLabel: "В процессе"
  },
  {
    patient: "Васильев К. Д.",
    procedure: "Массаж классический",
    place: "Каб. 115 · Соколов А.",
    time: "11:45",
    status: "done",
    statusLabel: "Завершено"
  },
  {
    patient: "Федорова Е. С.",
    procedure: "Ванна жемчужная",
    place: "Каб. 108 · Баранова М.",
    time: "12:00",
    status: "waiting",
    statusLabel: "Ожидает"
  }
];

const ALERTS = [
  {
    tone: "danger",
    icon: alertTriangleSrc,
    title: "Авария водопровода",
    text: "Второй корпус, перекрыт стояк на 3 этаже. Вызвана дежурная бригада."
  },
  {
    tone: "warning",
    icon: clockSrc,
    title: "Зависла бронь #1209",
    text: "Гость Смирнов И.В. не внес предоплату за 48 часов. Снятие через 2 часа."
  },
  {
    tone: "info",
    icon: sparklesSrc,
    title: "Успешная синхронизация",
    text: "Обмен данными с порталом Минздрава РФ завершен без ошибок."
  }
];

const RING_R = 32.76;
const RING_C = 2 * Math.PI * RING_R;

const formatTodayLabel = () => {
  const raw = dayjs().format("dddd, D MMMM YYYY [г.]");
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

const pluralRu = (n, one, few, many) => {
  const abs = Math.abs(Number(n) || 0) % 100;
  const d = abs % 10;
  if (abs > 10 && abs < 20) return many;
  if (d === 1) return one;
  if (d >= 2 && d <= 4) return few;
  return many;
};

const formatKpiMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${n.toLocaleString("ru-RU", { maximumFractionDigits: 0 })} ₽`;
};

const formatSignedMoney = (value, vid) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const signed = vid < 0 ? -Math.abs(n) : n;
  const formatted = formatMoney(Math.abs(signed));
  return `${signed < 0 ? "−" : ""}${formatted} ₽`;
};

const formatPercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${n.toLocaleString("ru-RU", { maximumFractionDigits: 0 })}%`;
};

const monthNameAt = (year, month) => {
  const d = dayjs(new Date(year, month - 1, 1));
  return d.isValid() ? d.format("MMMM") : "";
};

const HomeIcon = ({ src }) => (
  <span className="home__icon">
    <img src={src} alt="" />
  </span>
);

const DemoBadge = () => <span className="home__demo">Демо</span>;

const OccupancyRing = ({ percent }) => {
  const has = percent != null && Number.isFinite(Number(percent));
  const pct = has ? Math.max(0, Math.min(100, Number(percent))) : 0;
  return (
    <div className="home__ring" aria-hidden>
      <svg className="home__ring-svg" viewBox="0 0 72 72">
        <circle className="home__ring-track" cx="36" cy="36" r={RING_R} />
        <circle
          className="home__ring-arc"
          cx="36"
          cy="36"
          r={RING_R}
          strokeDasharray={`${(RING_C * pct) / 100} ${RING_C}`}
        />
      </svg>
      <span className="home__ring-label">{has ? `${Math.round(pct)}%` : "—"}</span>
    </div>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const license = useSelector((state) => state.license.data);
  const law = useSelector((state) => state.user.session?.law);
  const canSellBook = getLawFlag(law, 30) === 1;
  const canHealth = hasLicensedModule(license, PROGRAM_MODULES.HEALTH);
  const [sellOpen, setSellOpen] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [dashError, setDashError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    fetchHomeDashboard(controller.signal)
      .then((data) => {
        setDashboard(data || {});
        setDashError("");
      })
      .catch((e) => {
        if (controller.signal.aborted || e?.name === "AbortError") return;
        setDashError(e.message || "Не удалось загрузить сводку");
      });
    return () => controller.abort();
  }, []);

  const open = (url) => navigate(url);
  const loaded = dashboard != null;
  const pending = !loaded && !dashError;
  const placeholder = pending ? "Загрузка…" : "—";
  const companyName = (pick(dashboard, "companyName", "CompanyName") || "").trim() || "Проффитель";
  const occupancy = pick(dashboard, "occupancy", "Occupancy") || {};
  const arrivals = pick(dashboard, "arrivals", "Arrivals") || {};
  const paymentsToday = pick(dashboard, "paymentsToday", "PaymentsToday") || {};
  const analytics = pick(dashboard, "analytics", "Analytics") || {};
  const transactions = Array.isArray(pick(dashboard, "transactions", "Transactions"))
    ? pick(dashboard, "transactions", "Transactions")
    : [];

  const roomsTotal = toNumber(pick(occupancy, "roomsTotal", "RoomsTotal")) ?? 0;
  const roomsOccupied = toNumber(pick(occupancy, "roomsOccupied", "RoomsOccupied")) ?? 0;
  const occPercent = toNumber(pick(occupancy, "percent", "Percent"));
  const guests = toNumber(pick(arrivals, "guests", "Guests")) ?? 0;
  const pendingGuests = toNumber(pick(arrivals, "pendingGuests", "PendingGuests")) ?? 0;
  const payAmount = toNumber(pick(paymentsToday, "amount", "Amount"));
  const payDelta = toNumber(pick(paymentsToday, "percentVsPrevMonth", "PercentVsPrevMonth"));
  const prevMonth = toNumber(pick(paymentsToday, "prevMonth", "PrevMonth"));
  const prevYear = toNumber(pick(paymentsToday, "prevYear", "PrevYear"));
  const analyticsMonth = toNumber(pick(analytics, "month", "Month")) || dayjs().month() + 1;
  const analyticsYear = toNumber(pick(analytics, "year", "Year")) || dayjs().year();
  const repeatShare = toNumber(pick(analytics, "repeatShare", "RepeatShare"));
  const averageCheck = toNumber(pick(analytics, "averageCheck", "AverageCheck"));
  const paidShare = toNumber(pick(analytics, "paidShare", "PaidShare"));

  const arrivalsHint =
    pendingGuests > 0
      ? `Ожидается еще ${pendingGuests} ${pluralRu(pendingGuests, "прибытие", "прибытия", "прибытий")}`
      : guests > 0
        ? "Все заезды на сегодня выполнены"
        : "Нет заездов на сегодня";

  const payHint = () => {
    if (payDelta == null) return "Нет данных для сравнения с прошлым месяцем";
    const abs = Math.abs(payDelta).toLocaleString("ru-RU", { maximumFractionDigits: 1 });
    const month = monthNameAt(prevYear, prevMonth);
    const sign = payDelta > 0 ? "+" : payDelta < 0 ? "−" : "";
    return `${sign}${abs}% к среднедневным${month ? ` за ${month}` : ""}`;
  };

  return (
    <div className="home">
      <header className="home__header">
        <div className="home__header-text">
          <h1 className="home__title">Панель управления санаторием «{companyName}»</h1>
          <p className="home__subtitle">Оперативная сводка на сегодня · {formatTodayLabel()}</p>
        </div>
        <div className="home__actions">
          <button
            type="button"
            className="home__btn home__btn--primary"
            disabled={!canSellBook}
            title={canSellBook ? undefined : "Нет права на продажу + бронь"}
            onClick={() => canSellBook && setSellOpen(true)}
          >
            <HomeIcon src={plusSrc} />
            Быстрая бронь
          </button>
          <button
            type="button"
            className="home__btn home__btn--secondary"
            disabled={!canHealth}
            title={canHealth ? undefined : "Нет в лицензии"}
            onClick={() => open("/health/clients/new")}
          >
            <HomeIcon src={stethoscopeSrc} />
            Регистрация пациента
          </button>
        </div>
      </header>

      {dashError ? <div className="home__error">{dashError}</div> : null}

      <section className="home__kpis" aria-label="Ключевые показатели">
        <article className="home__kpi home__kpi--occupancy">
          <div className="home__kpi-info">
            <div className="home__kpi-label">Загрузка номеров</div>
            <div className="home__kpi-value">{loaded ? `${occPercent ?? 0}%` : "—"}</div>
            <div className="home__kpi-hint home__kpi-hint--accent">
              {loaded
                ? `${roomsOccupied} из ${roomsTotal} ${pluralRu(roomsTotal, "номера", "номеров", "номеров")} занято`
                : placeholder}
            </div>
          </div>
          <OccupancyRing percent={loaded ? occPercent : null} />
        </article>

        <article className="home__kpi">
          <div className="home__kpi-heading">
            <div className="home__kpi-label">Прибытие / Заезд</div>
            <span className="home__pill home__pill--blue">
              <HomeIcon src={arrowDownLeftSrc} />
            </span>
          </div>
          <div className="home__kpi-value">
            {loaded ? `${guests} ${pluralRu(guests, "гость", "гостя", "гостей")}` : "—"}
          </div>
          <div className="home__kpi-hint">{loaded ? arrivalsHint : placeholder}</div>
        </article>

        <article className="home__kpi">
          <div className="home__kpi-heading">
            <div className="home__kpi-label-row">
              <div className="home__kpi-label">Назначено процедур</div>
              <DemoBadge />
            </div>
            <span className="home__pill home__pill--green">
              <HomeIcon src={hospitalSrc} />
            </span>
          </div>
          <div className="home__kpi-value">412 сеансов</div>
          <div className="home__kpi-hint">Пиковая загрузка кабинетов: 11:00 - 13:00</div>
        </article>

        <article className="home__kpi">
          <div className="home__kpi-heading">
            <div className="home__kpi-label">Оплата за сегодня</div>
            <span className="home__pill home__pill--amber">
              <HomeIcon src={walletSrc} />
            </span>
          </div>
          <div className="home__kpi-value">{loaded ? formatKpiMoney(payAmount) : "—"}</div>
          <div
            className={`home__kpi-hint${payDelta != null && payDelta >= 0 ? " home__kpi-hint--accent" : ""}`}
          >
            {loaded ? payHint() : placeholder}
          </div>
        </article>
      </section>

      <div className="home__columns">
        <div className="home__main-col">
          <section className="home__card">
            <div className="home__card-header">
              <div className="home__card-title-wrap">
                <h2 className="home__card-title">Ближайшие лечебные сеансы</h2>
                <DemoBadge />
              </div>
              <button
                type="button"
                className="home__card-link"
                disabled={!canHealth}
                onClick={() => open("/health/clients")}
              >
                Все записи (412) →
              </button>
            </div>
            <div className="home__table home__table--sessions">
              <div className="home__thead">
                <span>Пациент</span>
                <span>Процедура</span>
                <span>Кабинет / Врач</span>
                <span>Время</span>
                <span className="home__cell--right">Статус</span>
              </div>
              {SESSIONS.map((row) => (
                <div className="home__trow" key={`${row.patient}-${row.time}`}>
                  <span className="home__cell-strong">{row.patient}</span>
                  <span>{row.procedure}</span>
                  <span>{row.place}</span>
                  <span className="home__cell-strong">{row.time}</span>
                  <span className="home__cell--right">
                    <span className={`home__badge home__badge--${row.status}`}>{row.statusLabel}</span>
                  </span>
                </div>
              ))}
            </div>
          </section>

          <section className="home__card">
            <div className="home__card-header">
              <h2 className="home__card-title">Последние финансовые транзакции</h2>
              <button type="button" className="home__card-link" onClick={() => open("/doc/plat")}>
                Все платежи →
              </button>
            </div>
            <div className="home__table home__table--payments">
              <div className="home__thead">
                <span>ID операции</span>
                <span>Плательщик / Назначение</span>
                <span>Способ</span>
                <span className="home__cell--right">Сумма</span>
              </div>
              {!loaded ? (
                <div className="home__empty">{placeholder}</div>
              ) : transactions.length === 0 ? (
                <div className="home__empty">Нет платежей</div>
              ) : (
                transactions.map((row) => {
                  const idReg = toNumber(pick(row, "idReg", "IdReg"));
                  const predName = (pick(row, "predName", "PredName") || "").trim();
                  const purpose = (pick(row, "purpose", "Purpose") || "").trim();
                  const payer = [predName, purpose].filter(Boolean).join(" · ") || "Без плательщика";
                  const kassaName = (pick(row, "kassaName", "KassaName") || "").trim();
                  const vid = toNumber(pick(row, "vid", "Vid")) ?? 0;
                  const method = kassaName || VID_PLAT_META[vid]?.label || "—";
                  return (
                    <div className="home__trow" key={idReg}>
                      <span className="home__cell-muted">#{idReg}</span>
                      <span className="home__cell-strong">{payer}</span>
                      <span>{method}</span>
                      <span className="home__cell-strong home__cell--right">
                        {formatSignedMoney(pick(row, "symaExp", "SymaExp"), vid)}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </section>
        </div>

        <aside className="home__side-col">
          <section className="home__card">
            <div className="home__card-title-wrap">
              <h2 className="home__card-title">События и уведомления</h2>
              <DemoBadge />
            </div>
            <div className="home__alerts">
              {ALERTS.map((alert) => (
                <article className={`home__alert home__alert--${alert.tone}`} key={alert.title}>
                  <span className="home__alert-icon">
                    <HomeIcon src={alert.icon} />
                  </span>
                  <div className="home__alert-body">
                    <div className="home__alert-title">{alert.title}</div>
                    <p className="home__alert-text">{alert.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="home__card">
            <h2 className="home__card-title">
              Экспресс-аналитика за {monthNameAt(analyticsYear, analyticsMonth)}
            </h2>
            <dl className="home__metrics">
              <div className="home__metric">
                <dt>Повторные бронирования</dt>
                <dd className="home__metric-value home__metric-value--green">
                  {loaded ? formatPercent(repeatShare) : "—"}
                </dd>
              </div>
              <div className="home__metric">
                <dt>Средний чек гостя</dt>
                <dd className="home__metric-value">{loaded ? formatKpiMoney(averageCheck) : "—"}</dd>
              </div>
              <div className="home__metric">
                <dt>Собрано оплаты</dt>
                <dd className="home__metric-value home__metric-value--blue">
                  {loaded ? formatPercent(paidShare) : "—"}
                </dd>
              </div>
            </dl>
          </section>
        </aside>
      </div>

      <DocExpSellBookModal
        open={sellOpen}
        asDrawer
        mode="category"
        onCancel={() => setSellOpen(false)}
        onDone={(saved) => {
          setSellOpen(false);
          const newId = toNumber(saved?.idDoc ?? saved?.IdDoc);
          if (newId != null) navigate(`/doc/exp/${newId}`);
        }}
      />
    </div>
  );
};

export default Home;
